import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { BreadCrumb } from "primereact/breadcrumb";
import { Card } from "primereact/card";
import { Tag } from "primereact/tag";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Calendar } from "primereact/calendar";
import { Toast } from "primereact/toast";
import { Dialog } from "primereact/dialog";
import { confirmDialog, ConfirmDialog } from "primereact/confirmdialog";
import { TabView, TabPanel } from "primereact/tabview";
import { Badge } from "primereact/badge";
import { ProgressBar } from "primereact/progressbar";
import { Avatar } from "primereact/avatar";
import { renewalMockData, renewalCrudOperations } from "../../../services/mockData/renewalMockData";
import SvgDot from "../../../assets/icons/SvgDot";
import "./index.scss";

const RenewalQueue = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("All");
  const [dateRange, setDateRange] = useState([null, null]);
  const [riskLevel, setRiskLevel] = useState("All");
  const [agent, setAgent] = useState("All");
  const [loading, setLoading] = useState(false);
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [detailsVisible, setDetailsVisible] = useState(false);
  const [sendReminderVisible, setSendReminderVisible] = useState(false);
  const [reminderMethod, setReminderMethod] = useState("Email");
  const [dashboardData, setDashboardData] = useState({
    totalPolicies: 0,
    dueSoon: 0,
    atRisk: 0,
    inGracePeriod: 0
  });
  const [filteredPolicies, setFilteredPolicies] = useState([]);
  const [policies, setPolicies] = useState(renewalMockData.renewalQueue);
  const toast = useRef(null);

  const statusOptions = [
    { label: "All Statuses", value: "All" },
    { label: "Pending", value: "Pending" },
    { label: "Quote Sent", value: "Quote Sent" },
    { label: "At Risk", value: "At Risk" },
    { label: "Under Negotiation", value: "Under Negotiation" },
    { label: "In Grace Period", value: "In Grace Period" }
  ];

  const riskOptions = [
    { label: "All Risk Levels", value: "All" },
    { label: "Low", value: "Low" },
    { label: "Medium", value: "Medium" },
    { label: "High", value: "High" },
    { label: "Critical", value: "Critical" }
  ];

  const agentOptions = [
    { label: "All Agents", value: "All" },
    { label: "Juan Dela Cruz", value: "Juan Dela Cruz" },
    { label: "Ana Reyes", value: "Ana Reyes" },
    { label: "Carlos Mendoza", value: "Carlos Mendoza" }
  ];

  const reminderMethods = [
    { label: "Email", value: "Email" },
    { label: "SMS", value: "SMS" },
    { label: "Phone", value: "Phone" },
    { label: "Letter", value: "Letter" }
  ];

  const items = [
    { label: "Renewals", url: "#" },
    { label: "Queue Management", url: "#" }
  ];

  const home = { icon: <SvgDot />, url: "#" };

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [search, status, dateRange, riskLevel, agent, policies]);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      // Simulate loading
      setTimeout(() => {
        calculateDashboard(policies);
        setLoading(false);
        toast.current.show({
          severity: 'success',
          summary: 'Data Loaded',
          detail: 'Renewal queue loaded successfully',
          life: 3000
        });
      }, 1000);
    } catch (error) {
      toast.current.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to load renewal data',
        life: 3000
      });
      setLoading(false);
    }
  };

  const calculateDashboard = (data) => {
    const dueSoon = data.filter(p => p.daysToExpiry <= 30 && p.daysToExpiry > 0);
    const atRisk = data.filter(p => p.retentionRisk === 'High' || p.retentionRisk === 'Critical');
    const inGrace = data.filter(p => p.status === 'In Grace Period');

    setDashboardData({
      totalPolicies: data.length,
      dueSoon: dueSoon.length,
      atRisk: atRisk.length,
      inGracePeriod: inGrace.length
    });
  };

  const applyFilters = () => {
    let filtered = [...policies];

    if (search) {
      filtered = filtered.filter(p =>
        p.policyNumber.toLowerCase().includes(search.toLowerCase()) ||
        p.insuredName.toLowerCase().includes(search.toLowerCase()) ||
        p.product.toLowerCase().includes(search.toLowerCase())
      );
    }

    if (status !== 'All') {
      filtered = filtered.filter(p => p.status === status);
    }

    if (riskLevel !== 'All') {
      filtered = filtered.filter(p => p.retentionRisk === riskLevel);
    }

    if (agent !== 'All') {
      filtered = filtered.filter(p => p.assignedAgent === agent);
    }

    if (dateRange[0] && dateRange[1]) {
      filtered = filtered.filter(p => {
        const expiry = new Date(p.expiryDate);
        return expiry >= dateRange[0] && expiry <= dateRange[1];
      });
    }

    setFilteredPolicies(filtered);
    calculateDashboard(filtered);
  };

  const handleClear = () => {
    setSearch("");
    setStatus("All");
    setDateRange([null, null]);
    setRiskLevel("All");
    setAgent("All");
    toast.current.show({
      severity: 'info',
      summary: 'Filters Cleared',
      detail: 'All filters have been reset',
      life: 2000
    });
  };

  const handleView = (rowData) => {
    setSelectedPolicy(rowData);
    setDetailsVisible(true);
  };

  const handleGenerateQuote = (rowData) => {
    navigate('/renewals/quote-generation', { state: { policy: rowData } });
  };

  const handleSendReminder = (rowData) => {
    setSelectedPolicy(rowData);
    setSendReminderVisible(true);
  };

  const handleSendReminderConfirm = async () => {
    setLoading(true);
    try {
      const result = await renewalCrudOperations.sendReminder(
        selectedPolicy.id,
        reminderMethod
      );

      // Update renewal attempts
      const updatedPolicies = policies.map(p =>
        p.id === selectedPolicy.id
          ? { ...p, renewalAttempts: p.renewalAttempts + 1, lastContactDate: new Date().toISOString().split('T')[0] }
          : p
      );
      setPolicies(updatedPolicies);

      setSendReminderVisible(false);
      toast.current.show({
        severity: 'success',
        summary: 'Reminder Sent',
        detail: `Renewal reminder sent via ${reminderMethod} to ${selectedPolicy.insuredName}`,
        life: 3000
      });
    } catch (error) {
      toast.current.show({
        severity: 'error',
        summary: 'Failed',
        detail: 'Failed to send reminder',
        life: 3000
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePriorityChange = (rowData, newPriority) => {
    confirmDialog({
      message: `Change priority for ${rowData.policyNumber} to ${newPriority}?`,
      header: 'Confirm Priority Change',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        // Update priority logic here
        toast.current.show({
          severity: 'success',
          summary: 'Priority Updated',
          detail: `Priority changed to ${newPriority}`,
          life: 3000
        });
      }
    });
  };

  const daysToExpiryBodyTemplate = (rowData) => {
    const days = rowData.daysToExpiry;
    let severity = 'success';
    let icon = 'pi-calendar';

    if (days < 0) {
      severity = 'danger';
      icon = 'pi-exclamation-triangle';
    } else if (days <= 7) {
      severity = 'danger';
      icon = 'pi-clock';
    } else if (days <= 15) {
      severity = 'warning';
      icon = 'pi-clock';
    } else if (days <= 30) {
      severity = 'info';
    }

    return (
      <div className="days-to-expiry">
        <i className={`pi ${icon} expiry-icon ${severity}`}></i>
        <span className={`expiry-text ${severity}`}>
          {days < 0 ? `${Math.abs(days)} days overdue` : `${days} days`}
        </span>
      </div>
    );
  };

  const statusBodyTemplate = (rowData) => {
    const getSeverity = (status) => {
      switch (status) {
        case 'Quote Sent': return 'info';
        case 'Under Negotiation': return 'warning';
        case 'At Risk': return 'danger';
        case 'In Grace Period': return 'danger';
        case 'Pending': return 'secondary';
        default: return 'secondary';
      }
    };

    return <Tag value={rowData.status} severity={getSeverity(rowData.status)} />;
  };

  const riskBodyTemplate = (rowData) => {
    const getSeverity = (risk) => {
      switch (risk) {
        case 'Low': return 'success';
        case 'Medium': return 'warning';
        case 'High': return 'danger';
        case 'Critical': return 'danger';
        default: return 'secondary';
      }
    };

    return <Tag value={rowData.retentionRisk} severity={getSeverity(rowData.retentionRisk)} />;
  };

  const premiumBodyTemplate = (rowData) => {
    return new Intl.NumberFormat('en-PH', {
      style: 'currency',
      currency: 'PHP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(rowData.currentPremium);
  };

  const agentBodyTemplate = (rowData) => {
    return (
      <div className="agent-cell">
        <Avatar label={rowData.assignedAgent.split(' ').map(n => n[0]).join('')}
                size="small" shape="circle" />
        <span>{rowData.assignedAgent}</span>
      </div>
    );
  };

  const attemptsBodyTemplate = (rowData) => {
    const maxAttempts = 3;
    const percentage = (rowData.renewalAttempts / maxAttempts) * 100;

    return (
      <div className="attempts-cell">
        <ProgressBar value={percentage} style={{ width: '60px', height: '8px' }} />
        <span>{rowData.renewalAttempts}/{maxAttempts}</span>
      </div>
    );
  };

  const actionBodyTemplate = (rowData) => {
    return (
      <div className="action-buttons">
        <Button
          icon="pi pi-eye"
          className="p-button-text"
          onClick={() => handleView(rowData)}
          tooltip="View Details"
        />
        <Button
          icon="pi pi-file-o"
          className="p-button-text"
          onClick={() => handleGenerateQuote(rowData)}
          tooltip="Generate Quote"
          disabled={rowData.status === 'Quote Sent'}
        />
        <Button
          icon="pi pi-send"
          className="p-button-text"
          onClick={() => handleSendReminder(rowData)}
          tooltip="Send Reminder"
        />
      </div>
    );
  };

  const policyLinkTemplate = (rowData) => {
    return (
      <a href="#" className="policy-link" onClick={(e) => {
        e.preventDefault();
        handleView(rowData);
      }}>
        {rowData.policyNumber}
      </a>
    );
  };

  const reminderDialogFooter = (
    <div className="dialog-footer">
      <Button
        label="Cancel"
        icon="pi pi-times"
        className="p-button-text"
        onClick={() => setSendReminderVisible(false)}
      />
      <Button
        label="Send Reminder"
        icon="pi pi-send"
        onClick={handleSendReminderConfirm}
        loading={loading}
      />
    </div>
  );

  return (
    <div className="container__renewal__queue__master">
      <Toast ref={toast} />
      <ConfirmDialog />

      <div className="top__container">
        <h1 className="page__title">Renewal Queue</h1>
        <BreadCrumb model={items} home={home} />
      </div>

      <div className="content-container">
        <div className="search-section">
          <Card>
            <div className="search-grid">
              <div className="search-field">
                <label>Policy/Insured Name</label>
                <InputText
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search policies or names"
                />
              </div>

              <div className="search-field">
                <label>Status</label>
                <Dropdown
                  value={status}
                  onChange={(e) => setStatus(e.value)}
                  options={statusOptions}
                  placeholder="Select status"
                />
              </div>

              <div className="search-field">
                <label>Risk Level</label>
                <Dropdown
                  value={riskLevel}
                  onChange={(e) => setRiskLevel(e.value)}
                  options={riskOptions}
                  placeholder="Select risk level"
                />
              </div>

              <div className="search-field">
                <label>Agent</label>
                <Dropdown
                  value={agent}
                  onChange={(e) => setAgent(e.value)}
                  options={agentOptions}
                  placeholder="Select agent"
                />
              </div>

              <div className="search-field">
                <label>Expiry Date Range</label>
                <Calendar
                  value={dateRange}
                  onChange={(e) => setDateRange(e.value)}
                  selectionMode="range"
                  dateFormat="mm/dd/yy"
                  placeholder="Select date range"
                />
              </div>

              <div className="search-actions">
                <Button label="Search" icon="pi pi-search" onClick={applyFilters} />
                <Button label="Clear" className="p-button-secondary" onClick={handleClear} />
              </div>
            </div>
          </Card>
        </div>

        <div className="dashboard-cards">
          <Card className="dashboard-card">
            <div className="card-content">
              <i className="pi pi-list card-icon blue"></i>
              <div className="card-info">
                <span className="card-value">{dashboardData.totalPolicies}</span>
                <span className="card-label">Total Policies</span>
              </div>
            </div>
          </Card>

          <Card className="dashboard-card clickable" onClick={() => setStatus('Pending')}>
            <div className="card-content">
              <i className="pi pi-clock card-icon orange"></i>
              <div className="card-info">
                <span className="card-value">{dashboardData.dueSoon}</span>
                <span className="card-label">Due Soon (30 days)</span>
              </div>
            </div>
          </Card>

          <Card className="dashboard-card clickable" onClick={() => setRiskLevel('High')}>
            <div className="card-content">
              <i className="pi pi-exclamation-triangle card-icon red"></i>
              <div className="card-info">
                <span className="card-value">{dashboardData.atRisk}</span>
                <span className="card-label">At Risk</span>
              </div>
            </div>
          </Card>

          <Card className="dashboard-card clickable" onClick={() => setStatus('In Grace Period')}>
            <div className="card-content">
              <i className="pi pi-ban card-icon dark-red"></i>
              <div className="card-info">
                <span className="card-value">{dashboardData.inGracePeriod}</span>
                <span className="card-label">In Grace Period</span>
              </div>
            </div>
          </Card>
        </div>

        <div className="table-section">
          <Card>
            <div className="table-header">
              <h3>Policies Due for Renewal</h3>
              <div className="table-actions">
                <Button
                  icon="pi pi-refresh"
                  className="p-button-text"
                  onClick={loadInitialData}
                  tooltip="Refresh"
                />
                <Button
                  icon="pi pi-file-excel"
                  className="p-button-text"
                  onClick={() => {
                    toast.current.show({
                      severity: 'success',
                      summary: 'Export Started',
                      detail: 'Renewal queue exported to Excel',
                      life: 3000
                    });
                  }}
                  tooltip="Export to Excel"
                />
              </div>
            </div>

            <DataTable
              value={filteredPolicies.length > 0 ? filteredPolicies : policies}
              className="renewal-table"
              stripedRows
              paginator
              rows={10}
              loading={loading}
              emptyMessage="No policies found"
              sortMode="multiple"
            >
              <Column
                body={policyLinkTemplate}
                header="Policy Number"
                style={{ width: '12%' }}
                sortable
                sortField="policyNumber"
              />
              <Column
                field="insuredName"
                header="Insured Name"
                style={{ width: '14%' }}
                sortable
              />
              <Column
                field="product"
                header="Product"
                style={{ width: '12%' }}
                sortable
              />
              <Column
                field="insurer"
                header="Insurer"
                style={{ width: '10%' }}
                sortable
              />
              <Column
                body={daysToExpiryBodyTemplate}
                header="Days to Expiry"
                style={{ width: '10%' }}
                sortable
                sortField="daysToExpiry"
              />
              <Column
                body={premiumBodyTemplate}
                header="Premium"
                style={{ width: '10%', textAlign: 'right' }}
                sortable
                sortField="currentPremium"
              />
              <Column
                body={statusBodyTemplate}
                header="Status"
                style={{ width: '10%' }}
              />
              <Column
                body={riskBodyTemplate}
                header="Risk"
                style={{ width: '8%' }}
              />
              <Column
                body={agentBodyTemplate}
                header="Agent"
                style={{ width: '10%' }}
              />
              <Column
                body={attemptsBodyTemplate}
                header="Attempts"
                style={{ width: '8%' }}
              />
              <Column
                body={actionBodyTemplate}
                header="Actions"
                style={{ width: '6%' }}
              />
            </DataTable>
          </Card>
        </div>

        {/* Policy Details Dialog */}
        <Dialog
          header="Policy Details"
          visible={detailsVisible}
          onHide={() => setDetailsVisible(false)}
          style={{ width: '70vw' }}
        >
          {selectedPolicy && (
            <TabView>
              <TabPanel header="Policy Information">
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Policy Number:</label>
                    <span>{selectedPolicy.policyNumber}</span>
                  </div>
                  <div className="detail-item">
                    <label>Insured Name:</label>
                    <span>{selectedPolicy.insuredName}</span>
                  </div>
                  <div className="detail-item">
                    <label>Product:</label>
                    <span>{selectedPolicy.product}</span>
                  </div>
                  <div className="detail-item">
                    <label>Insurer:</label>
                    <span>{selectedPolicy.insurer}</span>
                  </div>
                  <div className="detail-item">
                    <label>Expiry Date:</label>
                    <span>{new Date(selectedPolicy.expiryDate).toLocaleDateString()}</span>
                  </div>
                  <div className="detail-item">
                    <label>Current Premium:</label>
                    <span>{new Intl.NumberFormat('en-PH', {
                      style: 'currency',
                      currency: 'PHP'
                    }).format(selectedPolicy.currentPremium)}</span>
                  </div>
                  <div className="detail-item">
                    <label>Sum Insured:</label>
                    <span>{new Intl.NumberFormat('en-PH', {
                      style: 'currency',
                      currency: 'PHP'
                    }).format(selectedPolicy.sumInsured)}</span>
                  </div>
                  <div className="detail-item">
                    <label>Loyalty Years:</label>
                    <span>{selectedPolicy.loyaltyYears} years</span>
                  </div>
                </div>
              </TabPanel>

              <TabPanel header="Contact Information">
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Mobile:</label>
                    <span>{selectedPolicy.insuredContact.mobile}</span>
                  </div>
                  <div className="detail-item">
                    <label>Email:</label>
                    <span>{selectedPolicy.insuredContact.email}</span>
                  </div>
                  <div className="detail-item">
                    <label>Preferred Contact:</label>
                    <span>{selectedPolicy.insuredContact.preferredContact}</span>
                  </div>
                  <div className="detail-item">
                    <label>Last Contact:</label>
                    <span>{selectedPolicy.lastContactDate || 'Not contacted'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Renewal Attempts:</label>
                    <span>{selectedPolicy.renewalAttempts}</span>
                  </div>
                  <div className="detail-item">
                    <label>Assigned Agent:</label>
                    <span>{selectedPolicy.assignedAgent}</span>
                  </div>
                </div>
              </TabPanel>

              {selectedPolicy.claimsHistory && (
                <TabPanel header="Claims History">
                  <div className="detail-grid">
                    <div className="detail-item">
                      <label>Claims Last Year:</label>
                      <span>{selectedPolicy.claimsHistory.hasClaimsLastYear ? 'Yes' : 'No'}</span>
                    </div>
                    <div className="detail-item">
                      <label>Total Claims:</label>
                      <span>{selectedPolicy.claimsHistory.totalClaims}</span>
                    </div>
                    <div className="detail-item">
                      <label>Claims Amount:</label>
                      <span>{new Intl.NumberFormat('en-PH', {
                        style: 'currency',
                        currency: 'PHP'
                      }).format(selectedPolicy.claimsHistory.claimsAmount)}</span>
                    </div>
                    {selectedPolicy.claimsHistory.claimsRatio && (
                      <div className="detail-item">
                        <label>Claims Ratio:</label>
                        <span>{selectedPolicy.claimsHistory.claimsRatio}%</span>
                      </div>
                    )}
                  </div>
                </TabPanel>
              )}
            </TabView>
          )}
        </Dialog>

        {/* Send Reminder Dialog */}
        <Dialog
          header="Send Renewal Reminder"
          visible={sendReminderVisible}
          onHide={() => setSendReminderVisible(false)}
          style={{ width: '400px' }}
          footer={reminderDialogFooter}
        >
          {selectedPolicy && (
            <div className="reminder-form">
              <div className="form-field">
                <label>Policy:</label>
                <span>{selectedPolicy.policyNumber} - {selectedPolicy.insuredName}</span>
              </div>
              <div className="form-field">
                <label>Reminder Method:</label>
                <Dropdown
                  value={reminderMethod}
                  onChange={(e) => setReminderMethod(e.value)}
                  options={reminderMethods}
                  style={{ width: '100%' }}
                />
              </div>
              <div className="form-field">
                <label>Contact Info:</label>
                <span>
                  {reminderMethod === 'Email' && selectedPolicy.insuredContact.email}
                  {reminderMethod === 'SMS' && selectedPolicy.insuredContact.mobile}
                  {reminderMethod === 'Phone' && selectedPolicy.insuredContact.mobile}
                  {reminderMethod === 'Letter' && 'Mailing address on file'}
                </span>
              </div>
            </div>
          )}
        </Dialog>
      </div>
    </div>
  );
};

export default RenewalQueue;